import { Ban, CalendarClock, CircleCheck, CircleDot, CircleSlash } from "lucide-react";
import type { AppointmentStatus } from "@taskiq/shared";
import { Badge } from "@/components/ui/Badge";

/**
 * An appointment's state, as a word and an icon.
 *
 * `overdue` is not a database value - it is `scheduled` with an end time in the
 * past, derived at render (`isOverdue`). The phone calls it the same thing, which
 * it did not always: two clients with two words for one derived state is the
 * drift the parity rule exists to catch. A column would need a cron task to keep
 * it true and would be wrong for however long that task took to run, which
 * is the worst kind of wrong: quietly, for a bounded period, on the records
 * somebody is auditing.
 *
 * Tones are borrowed from the severity ramp here, and that is fine in a way it
 * is not on an event chip: this badge carries a **word and an icon** as well, so
 * the hue is reinforcement rather than the message. The chip on the calendar is
 * the opposite case - there, colour is doing real work encoding the type, which
 * is exactly why status is expressed there through strike-through and opacity.
 */
export function StatusBadge({
  status,
  overdue = false,
}: {
  status: AppointmentStatus;
  overdue?: boolean;
}) {
  if (status === "cancelled") {
    return (
      <Badge tone="neutral" icon={<Ban aria-hidden />}>
        Cancelled
      </Badge>
    );
  }
  if (status === "completed") {
    return (
      <Badge tone="ok" icon={<CircleCheck aria-hidden />}>
        Completed
      </Badge>
    );
  }
  if (status === "in_progress") {
    return (
      <Badge tone="accent" icon={<CircleDot aria-hidden />}>
        In progress
      </Badge>
    );
  }
  if (overdue) {
    return (
      <Badge tone="caution" icon={<CircleSlash aria-hidden />}>
        Overdue
      </Badge>
    );
  }
  return (
    <Badge tone="neutral" icon={<CalendarClock aria-hidden />}>
      Scheduled
    </Badge>
  );
}
